import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Star, Mail, ExternalLink, Tag, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

const UNIT_LABEL = {
  per_month: "/ mois",
  per_year: "/ an",
  per_use: "/ utilisation",
  one_time: "paiement unique",
  on_quote: "sur devis",
};

export default function MemberSharedServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/shared-services")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Impossible de charger les services"))
      .finally(() => setLoading(false));
  }, []);

  // Distinct categories (manual + dynamic)
  const categories = Array.from(new Set(items.map((s) => s.category).filter(Boolean)));
  const filtered = filter ? items.filter((s) => s.category === filter) : items;

  return (
    <PortalLayout sidebar="member">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-wine-600">Réservé aux membres</div>
          <h1 className="mt-2 text-3xl font-bold text-navy md:text-4xl">Services mutualisés</h1>
          <p className="mt-2 max-w-2xl text-neutral-600">Profitez de la puissance du collectif La WineTech : achats groupés, conseil expert, assurances, outils mutualisés à tarifs négociés.</p>
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800">
          <Sparkles className="h-3.5 w-3.5" /> Avantage membre
        </div>
      </div>

      {categories.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="services-filter-bar">
          <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === "" ? "bg-wine-500 text-white" : "bg-white border border-neutral-200 text-neutral-600"}`} data-testid="services-filter-all">
            Toutes ({items.length})
          </button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === c ? "bg-wine-500 text-white" : "bg-white border border-neutral-200 text-neutral-600"}`} data-testid={`services-filter-${c}`}>
              {c}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-12 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-wine-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-12 rounded-2xl border border-neutral-100 bg-white p-10 text-center text-neutral-500" data-testid="services-empty">
          <Sparkles className="mx-auto h-10 w-10 text-neutral-300" />
          <p className="mt-3">Aucun service disponible pour le moment.</p>
          <p className="text-xs">Notre équipe travaille sur de nouveaux partenariats — revenez bientôt.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {filtered.map((s) => (
            <article key={s.id} className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`service-card-${s.id}`}>
              {s.image && (
                <div className="aspect-[16/10] overflow-hidden bg-wine-50">
                  <img src={resolveUrl(s.image)} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
              )}
              <div className="flex flex-1 flex-col p-5">
                <div className="flex flex-wrap items-center gap-2">
                  {s.category && <span className="inline-flex items-center gap-1 rounded-full bg-wine-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-wine-700"><Tag className="h-2.5 w-2.5" />{s.category}</span>}
                  {s.featured && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase text-amber-800"><Star className="h-2.5 w-2.5 fill-current" />Mise en avant</span>}
                </div>

                <h3 className="mt-3 font-serif text-xl leading-snug text-navy">{s.title}</h3>
                <p className="mt-2 line-clamp-4 text-sm text-neutral-600 whitespace-pre-line">{s.description}</p>

                <div className="mt-auto pt-5">
                  <div className="mb-3 border-t border-neutral-100 pt-4">
                    {s.variants?.length > 0 ? (
                      <ul className="space-y-2" data-testid={`service-variants-${s.id}`}>
                        {s.variants.map((v) => (
                          <li key={v.id} className="flex items-start justify-between gap-3 rounded-xl bg-neutral-50 px-3 py-2" data-testid={`service-variant-${v.id}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-navy">{v.name}</div>
                              {v.description && <div className="text-[11px] leading-snug text-neutral-500">{v.description}</div>}
                            </div>
                            <div className="shrink-0 text-right">
                              {v.price_unit === "on_quote" ? <span className="text-sm font-bold text-wine-600">Sur devis</span> : (
                                <><span className="font-serif text-lg font-bold text-navy">{Number(v.price || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} {s.currency || "EUR"}</span>
                                <div className="text-[10px] text-neutral-500">{UNIT_LABEL[v.price_unit] || ""}</div></>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : s.price_unit === "on_quote" ? (
                      <div className="text-lg font-bold text-wine-600">Sur devis</div>
                    ) : (
                      <div>
                        <span className="font-serif text-3xl font-bold text-navy">{Number(s.price || 0).toFixed(0)}</span>
                        <span className="ml-1 text-sm font-semibold text-neutral-700">{s.currency || "EUR"}</span>
                        <span className="ml-2 text-xs text-neutral-500">{UNIT_LABEL[s.price_unit] || ""}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.contact_email && (
                      <a href={`mailto:${s.contact_email}?subject=${encodeURIComponent("Demande d'info — " + s.title)}`} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid={`service-contact-${s.id}`}>
                        <Mail className="h-3.5 w-3.5" /> Demander un devis
                      </a>
                    )}
                    {s.cta_url && (
                      <a href={s.cta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-wine-300 bg-white px-4 py-2 text-xs font-bold text-wine-700 hover:bg-wine-50" data-testid={`service-cta-${s.id}`}>
                        <ExternalLink className="h-3.5 w-3.5" /> En savoir plus
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
